import { forkJoin as observableForkJoin, Subscription, Observable } from "rxjs";

import { map } from "rxjs/operators";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from "@angular/core";
import {
  ClientQuestionDropdownService,
  ClientQuestionService,
  Question,
  TaxReturnService
} from "../../../../../../swaggerServices";
import {
  ListOption,
  makeControlForQuestion
} from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { cloneDeep } from "lodash-es";
import { ClientRulesQuestionsResolver } from "../client-rules-questions-resolver";
import { environment } from "../../../../../environments/environment";
import { TenantConfigService } from "../../../../../shared/services/tenant-config.service";
import { ConfigStateService } from "../../../../config/config-state.service";
import { TranslocoService } from "@jsverse/transloco";

declare var $: any;

export interface IClientQuestion {
  id?: number;
  question: string;
  year?: number;
  mandatory?: boolean;
  filtering?: boolean;
  dependantMandatoryParentValue?: string;
  hidden?: boolean;
  isCurrency?: boolean;
  isPercentage?: boolean;
  min?: number;
  max?: number;
  maxLength?: number;
  valueFormat?: string;
  dataType?: string;
  clientCategoryId?: number;
  dependantMandatoryParentId?: number;
  parentId?: number;
  displayFieldType: string;
  default?: string;
  questionDropdowns?: ListOption[];
  clientQuestionDropdowns?: ListOption[];
  clientHint?: string;
  tag?: string;
  answers?: any[];
  topLevel?: boolean;
  status: number;
}

@Component({
    selector: "app-client-rules-question",
    templateUrl: "./client-rules-question.component.html",
    styleUrls: ["./client-rules-question.component.scss"],
    standalone: false
})
export class ClientRulesQuestionComponent implements OnInit, OnDestroy {
  question: IClientQuestion;
  originalQuestion: IClientQuestion;
  questions: IClientQuestion[];
  formGroup: UntypedFormGroup;
  prevFormGroup: UntypedFormGroup;
  dependanciesFormGroup: UntypedFormGroup;
  @ViewChild("questionTypeDropDown") questionTypeDropDown: ElementRef;
  @ViewChild("defaultValueDropdown") defaultValueDropdown: ElementRef;
  @ViewChild("numberOptionsDropDown") numberOptionsDropDown: ElementRef;
  @ViewChild("depMandQuestionDropDown") depMandQuestionDropDown: ElementRef;
  @ViewChild("depMandAnswerDropdown") depMandAnswerDropdown: ElementRef;
  @ViewChild("addQuestionModal") addQuestionModal: ElementRef;
  dependencyType: string;
  dependantMandatoryQuestion: IClientQuestion;
  tab: number = 1;
  clickedRow: boolean;
  questionType: UntypedFormControl;
  questionName: UntypedFormControl;
  mandatory: UntypedFormControl;
  filtering: UntypedFormControl;
  hidden: UntypedFormControl;
  default: UntypedFormControl;
  min: UntypedFormControl;
  max: UntypedFormControl;
  maxLength: UntypedFormControl;
  valueFormat: UntypedFormControl;
  allowDecimals: UntypedFormControl;
  clientHint: UntypedFormControl;
  tag: UntypedFormControl;
  isTable: UntypedFormControl;
  questionSearch: string;
  columnQuestions: IClientQuestion[] = []; // ids of questions which need to be saved with parentId on save
  isSaving: boolean;
  subscriptions: Subscription[] = [];
  isProductionMode: boolean;
  uiConfig: TenantConfigService;
  yearLabel: string;
  showDelete: boolean;
  isDeletedQuestion: boolean;
  public taxYear = signal<number | null>(null);

  constructor(
    private activatedRoute: ActivatedRoute,
    private config: ConfigStateService,
    private clientQuestionService: ClientQuestionService,
    private clientRulesQuestionsResolver: ClientRulesQuestionsResolver,
    private router: Router,
    private clientQuestionDropdownService: ClientQuestionDropdownService,
    private translocoService: TranslocoService
  ) {
    this.isProductionMode = environment.production;
  }

  ngOnInit() {
    this.subscriptions.push(
      this.clientRulesQuestionsResolver
        .getQuestionsObs()
        .subscribe((q: IClientQuestion) => this.initQuestion(q))
    );
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService
          .selectTranslate("year")
          .subscribe(value => (this.yearLabel = value));
        this.showDelete = config.featureFlags.allowSoftDelete;
      })
    );
  }

  getQuestionsByyear() {
    this.subscriptions.push(
      this.clientQuestionService
        .getClientQuestionsByyear(this.question.year)
        .subscribe((questions: any) => {
          this.questions = questions.clientQuestions;
          this.initDepQuestionDropdown(questions.clientQuestions);
          this.initTableQuestions();
        })
    );
  }

  // find all question table's column questions
  initTableQuestions() {
    this.columnQuestions = [];
    this.questions.forEach(q => {
      if (q.parentId === this.question.id) this.columnQuestions.push(q);
    });
  }

  unfocusValueRows() {
    if (!this.question.questionDropdowns) return;
    this.question.questionDropdowns.forEach(
      (v: ListOption) => (v.edit = false)
    );
  }

  getQuestionType(q: IClientQuestion): string {
    let questionType = q.displayFieldType;
    if (q.displayFieldType === "text") {
      if (q.dataType === "string") {
        questionType = "text";
      } else if (
        q.dataType.toLowerCase() === "integer" ||
        q.dataType.toLowerCase() === "float"
      ) {
        questionType = "number";
      }
    }
    return questionType;
  }

  initQuestion(q: IClientQuestion) {
    q.status === 99
      ? (this.isDeletedQuestion = true)
      : (this.isDeletedQuestion = false);
    q.questionDropdowns = q.clientQuestionDropdowns || [];
    q.questionDropdowns = q.questionDropdowns.sort((a, b) => a.order - b.order); // order dropdown values by selected order

    this.originalQuestion = cloneDeep(q);
    this.dependencyType =
      q.dependantMandatoryParentValue && q.dependantMandatoryParentId
        ? "specific"
        : "any";

    // answers is req by field-wrapper
    q.answers = [];
    this.question = q;
    this.taxYear.set(q.year ?? null)

    // for table questions, always refresh questions list
    if (this.question.displayFieldType === "table") {
      this.getQuestionsByyear();
    } else {
      if (!this.questions || this.questions.length === 0)
        this.getQuestionsByyear();
    }

    // init forms
    this.formGroup = new UntypedFormGroup({});
    this.prevFormGroup = new UntypedFormGroup({});
    this.dependanciesFormGroup = new UntypedFormGroup({});
    this.questionType = new UntypedFormControl(this.getQuestionType(q));
    this.questionName = new UntypedFormControl(this.question.question);
    this.mandatory = new UntypedFormControl(this.question.mandatory);
    this.filtering = new UntypedFormControl(this.question.filtering);
    this.hidden = new UntypedFormControl(this.question.hidden);
    this.default = new UntypedFormControl(this.question.default, {
      validators: Validators.compose([Validators.maxLength(255)])
    });
    this.min = new UntypedFormControl(this.question.min);
    this.max = new UntypedFormControl(this.question.max);
    this.maxLength = new UntypedFormControl(this.question.maxLength);
    this.clientHint = new UntypedFormControl(this.question.clientHint);
    this.tag = new UntypedFormControl(this.question.tag);
    this.valueFormat = new UntypedFormControl(this.question.valueFormat);
    this.allowDecimals = new UntypedFormControl();
    this.isTable = new UntypedFormControl(this.question.displayFieldType === "table");
    this.formGroup.addControl("questionType", this.questionType);
    this.formGroup.addControl("questionName", this.questionName);
    this.formGroup.addControl("mandatory", this.mandatory);
    this.formGroup.addControl("filtering", this.filtering);
    this.formGroup.addControl("hidden", this.hidden);
    this.formGroup.addControl("default", this.default);
    this.formGroup.addControl("maxLength", this.maxLength);
    this.formGroup.addControl("clientHint", this.clientHint);
    this.formGroup.addControl("tag", this.tag);
    this.formGroup.addControl("min", this.min);
    this.formGroup.addControl("max", this.max);
    this.formGroup.addControl("valueFormat", this.valueFormat);
    this.formGroup.addControl("allowDecimals", this.allowDecimals);
    this.formGroup.addControl("isTable", this.isTable);
    this.formGroup.addControl("displayFieldType", new UntypedFormControl());

    // dependencies tab form group
    const dependantMandatoryParentId = new UntypedFormControl();
    this.dependanciesFormGroup.addControl(
      "dependantMandatoryParentId",
      dependantMandatoryParentId
    );

    if (this.question.dataType === "Float") this.allowDecimals.setValue(true);
    else if (this.question.dataType === "integer")
      this.allowDecimals.setValue(false);

    this.formGroup.valueChanges.subscribe(formGroup => {
      Object.keys(formGroup).forEach(
        key => (this.question[key] = formGroup[key])
      );

      this.question.question = formGroup.questionName;
      this.question.mandatory = Boolean(formGroup.mandatory);
      this.question.filtering = Boolean(formGroup.filtering);

      if (formGroup.questionType === "number") {
        this.question.displayFieldType = "text";
        if (this.allowDecimals.value) {
          this.question.dataType = "Float";
        } else {
          this.question.dataType = "integer";
        }
      } else {
        this.question.displayFieldType = formGroup.questionType;
        this.question.dataType = "string";
        this.min.setValue(null, {
          emitEvent: false
        });
        this.max.setValue(null, {
          emitEvent: false
        });
        this.question.min = null;
        this.question.max = null;
      }

      if (formGroup.questionType === "checkbox" && formGroup.mandatory) {
        this.question.mandatory = false;
        this.mandatory.setValue("false", {
          emitEvent: false
        });
      }

      if (formGroup.questionType === "table") {
        this.mandatory.setValue(false, {
          emitEvent: false
        });
        this.filtering.setValue(false, {
          emitEvent: false
        });
        this.isTable.setValue(true, {
          emitEvent: false
        });
      } else {
        this.isTable.setValue(false, {
          emitEvent: false
        });
      }

      // update preview
      this.updatePreview();

      // need to re-init ui controls on values and table tab on question type change
      $(".ui.checkbox").checkbox();
    });

    // init all ui controls
    setTimeout(() => {
      this.initQuestionTypeDropdown();
      this.initDefaultValueDropdown();

      const values = [
        {
          name: "Select...",
          value: null,
          selected: !this.question.isCurrency && !this.question.isPercentage
        },
        {
          name: "Currency",
          value: "currency",
          selected: this.question.isCurrency
        },
        {
          name: "Percentage",
          value: "percentage",
          selected: this.question.isPercentage
        }
      ];

      $(this.numberOptionsDropDown.nativeElement).dropdown({
        placeholder: "Select...",
        fullTextSearch: "exact",
        preserveHTML: false,
        values: values,
        onChange: (value: string) => {
          this.question.isCurrency = value === "currency";
          this.question.isPercentage = value === "percentage";
        }
      });

      $("#page").click(() =>
        !this.clickedRow ? this.unfocusValueRows() : undefined
      );

      setTimeout(() => {
        $(".ui.checkbox").checkbox();
        $("#table.tab .new-value-btn").popup({
          on: "click",
          position: "right center"
        });
      });
      this.updatePreview();
    });
  }

  // re-init ui controls for tabs is required when question type changes
  goToTab(tabNum: number) {
    this.tab = tabNum;
  }

  initQuestionTypeDropdown() {
    const valuesOptions: {}[] = [
      {
        value: "text",
        name: "Text",
        selected: this.questionType.value === "text"
      },
      {
        value: "number",
        name: "Number",
        selected: this.questionType.value === "number"
      },
      {
        value: "textarea",
        name: "Textarea",
        selected: this.questionType.value === "textarea"
      },
      {
        value: "drop down",
        name: "Drop Down",
        selected: this.questionType.value === "drop down"
      },
      {
        value: "people",
        name: "People Picker",
        selected: this.questionType.value === "people"
      },
      {
        value: "radio",
        name: "Radio",
        selected: this.questionType.value === "radio"
      },
      {
        value: "date picker",
        name: "Date Picker",
        selected: this.questionType.value === "date picker"
      },
      {
        value: "checkbox",
        name: "Checkbox",
        selected: this.questionType.value === "checkbox"
      },
      {
        value: "file",
        name: "File",
        selected: this.questionType.value === "file"
      }
    ];

    // can only set unsaved questions to table
    if (
      !this.question.id ||
      (this.question.id && this.question.displayFieldType === "table")
    ) {
      valuesOptions.push({
        value: "table",
        name: "Table",
        selected: this.questionType.value === "table"
      });
    }

    $(this.questionTypeDropDown.nativeElement).dropdown({
      values: valuesOptions,
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        if (value) this.questionType.setValue(value);
      }
    });
  }

  initDefaultValueDropdown() {
    if (
      !this.question.questionDropdowns ||
      (this.getQuestionType(this.question) !== "drop down" &&
        this.getQuestionType(this.question) !== "radio")
    )
      return;

    const defaultValueDropdownOptions = this.question.questionDropdowns
      .filter(v => !v.disabled)
      .sort((a, b) => a.order - b.order)
      .map(v => {
        return {
          value: v.value,
          name: v.display,
          selected: v.value === this.question.default
        };
      });

    const valuesPlaceholder: {}[] = [
      {
        name: "Select...",
        value: null,
        order: 0
      }
    ];

    $(this.defaultValueDropdown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(defaultValueDropdownOptions),
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: string) => {
        this.default.setValue(value);
        // update values list to toggle default selected checkbox
        this.question.questionDropdowns.forEach(
          (v: ListOption) => (v.selected = v.value === value)
        );
      }
    });
  }

  // init the dropdown of all questions for dependant mandatory
  initDepQuestionDropdown(questions: IClientQuestion[]) {
    this.questions = questions;

    const valuesPlaceholder: {}[] = [
      {
        name: "Select...",
        value: 0,
        order: 0
      }
    ];

    this.dependantMandatoryQuestion = this.questions.find(
      q => q.id === Number(this.question.dependantMandatoryParentId)
    );
    const depMandQuestionDropDownVals = questions
      .filter(
        q =>
          q.id !== this.question.id &&
          !q.hidden &&
          q.displayFieldType !== "table" &&
          !q.parentId
      )
      .map(q => {
        return {
          value: q.id,
          name: q.question,
          selected: q.id === this.question.dependantMandatoryParentId
        };
      });

    $(this.depMandQuestionDropDown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(depMandQuestionDropDownVals),
      placeholder: "Select...",
      fullTextSearch: "exact",
      preserveHTML: false,
      onChange: (value: number) => {
        if (value) {
          this.dependantMandatoryQuestion = this.questions.find(
            q => q.id === Number(value)
          );
          if (value > 0) {
            this.question.dependantMandatoryParentId = Number(value);
          } else {
            this.question.dependantMandatoryParentId = null;
            this.question.dependantMandatoryParentValue = null;
            this.dependencyType = "any";
          }
          if (
            this.dependantMandatoryQuestion &&
            (this.dependantMandatoryQuestion.displayFieldType === "drop down" ||
              this.dependantMandatoryQuestion.displayFieldType === "radio")
          )
            this.initDepAnsDropdown();
        }
      }
    });
  }

  // get the dropdown values for the dependant question
  initDepAnsDropdown() {
    this.clientQuestionService
      .getClientQuestionWithDropdowns(this.dependantMandatoryQuestion.id)
      .subscribe((q: IClientQuestion) => {
        const depMandAnswerDropdownVals = q.clientQuestionDropdowns.map(q => {
          return {
            value: q.value,
            name: q.display,
            selected: q.value === this.question.dependantMandatoryParentValue
          };
        });

        $(this.depMandAnswerDropdown.nativeElement).dropdown({
          values: depMandAnswerDropdownVals,
          placeholder: "Select...",
          fullTextSearch: "exact",
          preserveHTML: false,
          onChange: (value: string) => {
            if (value) {
              this.question.dependantMandatoryParentValue = value;
            }
          }
        });
      });
  }

  // checks whether question's list options already contains a default selection
  questionHasDefault() {
    return this.question.questionDropdowns.some(d => d.selected);
  }

  deleteValue(index: number) {
    this.question.questionDropdowns[index].status = 99;
    this.initDefaultValueDropdown();
    this.updatePreview();
  }

  addValue() {
    this.question.questionDropdowns.unshift({
      id: null,
      value: null,
      display: null,
      order: null,
      selected: null,
      disabled: null
    });

    // focus first input
    setTimeout(() => {
      this.question.questionDropdowns[0]["edit"] = true;
      $("#tbl-container")
        .find("#tbl-container tbody tr:first-of-type td:first-of-type input")
        .focus();
      $(".ui.checkbox").checkbox();
      this.updatePreview();
    });
  }

  editValue(valueOption: ListOption, event) {
    this.unfocusValueRows();
    valueOption.edit = true;
    $(event.target)
      .find("input")
      .focus();
    this.clickedRow = true; // prevent unfocusValueRows() causing row to de-activate
    setTimeout(() => (this.clickedRow = false));
  }

  // only 1 option cab be defaut selected. toggle all other options
  toggleDefaultValue(valueOption: ListOption) {
    const value = cloneDeep(valueOption);
    this.question.questionDropdowns.forEach((v: ListOption) => {
      v.selected = false;
      if (v.id === value.id) v.selected = !value.selected;
      if (v.selected) this.question.default = value.value;
    });
    if (!this.question.questionDropdowns.some(v => v.selected))
      this.question.default = null;
    this.initDefaultValueDropdown();
  }

  // update question preview
  updatePreview() {
    if (!this.question) return;
    if (this.questionType.value !== "table") {
      this.prevFormGroup.removeControl(String(this.question.id));
      setTimeout(() => {
        this.prevFormGroup.addControl(
          String(this.question.id),
          makeControlForQuestion(this.question)
        );
      });
    }
  }

  addExistingQuestion() {
    $(this.addQuestionModal.nativeElement).modal("show");
    $("#table.tab .new-value-btn").popup("hide");
  }

  // question can only be added to table if it is not already used, is not a table itself, is not the same question id, and is not tab question
  canAddTableQuestion(question: IClientQuestion) {
    return (
      !this.columnQuestions.find(q => q.id === question.id) &&
      question.displayFieldType !== "table" &&
      !question.parentId
    );
  }

  // sets the table question as the selected question's parentId
  useQuestion(question) {
    this.columnQuestions.push({
      ...question,
      updateParentId: true
    });
    $(this.addQuestionModal.nativeElement).modal("hide");
  }

  // remove question from table
  removeQuestion(question) {
    this.columnQuestions = this.columnQuestions.filter(
      q => q.id !== question.id
    );
  }

  deleteQuestion(question: IClientQuestion) {
    let qUpdateObs = [];
    if (!this.isDeletedQuestion) {
      if (confirm("Are you sure you want to delete this question?")) {
        let questionsNeedDelete = [];
        questionsNeedDelete.push(question);
        if (question.displayFieldType === "table") {
          this.columnQuestions.forEach(cq => questionsNeedDelete.push(cq));
        }
        questionsNeedDelete.forEach(q => {
          qUpdateObs.push(
            this.clientQuestionService.updateClientQuestion({
              id: q.id,
              question: q.question,
              status: 99
            })
          );

          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          ) {
            qUpdateObs.push(
              this.clientQuestionDropdownService.updateAllDropdownStatusWithSameClientQuestionId(
                {
                  clientQuestionId: q.id,
                  status: 99
                }
              )
            );
          }
        });
      }
    } else {
      if (confirm("Are you sure you want to restore this question?")) {
        let questionsNeedRestore = [];
        questionsNeedRestore.push(question);
        if (question.displayFieldType === "table") {
          this.columnQuestions.forEach(cq => questionsNeedRestore.push(cq));
        }
        questionsNeedRestore.forEach(q => {
          qUpdateObs.push(
            this.clientQuestionService.updateClientQuestion({
              id: q.id,
              question: q.question,
              status: 0
            })
          );

          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          ) {
            qUpdateObs.push(
              this.clientQuestionDropdownService.updateAllDropdownStatusWithSameClientQuestionId(
                {
                  clientQuestionId: q.id,
                  status: 0
                }
              )
            );
          }
        });
      }
    }

    observableForkJoin(qUpdateObs).subscribe(() => {
      this.isDeletedQuestion = !this.isDeletedQuestion;
    });
  }

  save() {
    this.isSaving = true;

    const reqBody = {
      id: this.question.id,
      displayFieldType: this.question.displayFieldType,
      question: this.question.question,
      dataType: this.question.dataType,
      mandatory: this.question.mandatory,
      filtering: this.question.filtering,
      default: this.question.default,
      hidden: this.question.hidden,
      min: this.question.min,
      max: this.question.max,
      maxLength: this.question.maxLength,
      valueFormat: this.question.valueFormat,
      clientHint: this.question.clientHint,
      tag: this.question.tag,
      isCurrency: this.question.isCurrency,
      isPercentage: this.question.isPercentage,
      dependantMandatoryParentId: this.question.dependantMandatoryParentId,
      dependantMandatoryParentValue: this.question
        .dependantMandatoryParentValue,
      questionDropdowns: this.question.questionDropdowns,
      year: this.question.year
    };

    if (this.question.dependantMandatoryParentValue != null)
      reqBody.dependantMandatoryParentValue = String(
        this.question.dependantMandatoryParentValue
      );

    // convert to required data types
    if (this.getQuestionType(this.question) === "number") {
      const parseNumber = x => {
        if (x == null) {
          return null;
        }
        const n = Number(x);
        return Number.isFinite(n) ? n : null;
      };

      reqBody.default =
        reqBody.default != null ? String(reqBody.default) : null;
      reqBody.min = parseNumber(reqBody.min);
      reqBody.max = parseNumber(reqBody.max);
      reqBody.maxLength = parseNumber(reqBody.maxLength);
    } else if (this.getQuestionType(this.question) === "date picker") {
      reqBody.dataType = "date";
    } else if (this.getQuestionType(this.question) === "table") {
      reqBody.dataType = "table";
    }
    // tables require checkboxes to be boolean
    else if (this.getQuestionType(this.question) === "checkbox") {
      reqBody.dataType = "boolean";
    } else {
      reqBody.dataType = "string";
    }

    let qUpdateObs = [];
    const saveColumnQuestions = question => {
      this.columnQuestions.forEach((q: any) => {
        if (q.updateParentId) {
          qUpdateObs.push(
            this.clientQuestionService.updateClientQuestion({
              id: q.id,
              parentId: question.id,
              question: q.question, // api needs this passed in
              clientCategoryId: question.clientCategoryId
            })
          );
        }
      });
    };

    const updateDropdown = questionId => {
      if (this.question.questionDropdowns) {
        this.question.questionDropdowns.forEach(qD => {
          if (qD.id) {
            qUpdateObs.push(
              this.clientQuestionDropdownService.updateClientQuestionDropdown({
                id: qD.id,
                value: qD.value,
                display: qD.display,
                order: qD.order,
                selected: qD.selected,
                disabled: qD.disabled,
                clientQuestionId: questionId,
                status: qD.status
              })
            );
          } else {
            qUpdateObs.push(
              this.clientQuestionDropdownService.addClientQuestionDropdown({
                value: qD.value,
                display: qD.display,
                order: qD.order,
                selected: qD.selected,
                disabled: qD.disabled,
                clientQuestionId: questionId
              })
            );
          }
        });
      }
    };

    // for new question, need to get q id before saving table questions
    if (!this.question.id) {
      this.clientQuestionService.addClientQuestion(reqBody).subscribe(
        (q: IClientQuestion) => {
          if (q.displayFieldType === "table") saveColumnQuestions(q);
          if (
            q.displayFieldType === "drop down" ||
            q.displayFieldType === "radio"
          )
            updateDropdown(q.id);

          if (qUpdateObs.length > 0) {
            observableForkJoin(qUpdateObs).subscribe(
              () => {
                // refresh route to allow new question id to appear in url
                this.router.navigate([
                  "settings",
                  "client-rules",
                  "questions",
                  q.id
                ]);
                this.isSaving = false;
              },
              () => (this.isSaving = false)
            );
          } else {
            this.router.navigate([
              "settings",
              "client-rules",
              "questions",
              q.id
            ]);
            this.isSaving = false;
          }
        },
        () => (this.isSaving = false)
      );
    }

    // existing question
    else {
      if (this.question.displayFieldType === "table")
        saveColumnQuestions(this.question);
      if (
        this.question.displayFieldType === "drop down" ||
        this.question.displayFieldType === "radio"
      )
        updateDropdown(this.question.id);
      let savedQ;
      qUpdateObs.push(
        this.clientQuestionService
          .updateClientQuestion(reqBody)
          .pipe(map((q: Question) => (savedQ = q)))
      );

      observableForkJoin(qUpdateObs).subscribe(
        () => {
          this.clientQuestionService
            .getClientQuestionWithDropdowns(this.question.id)
            .subscribe((data: IClientQuestion) => {
              this.initQuestion(data);
              this.isSaving = false;
            });
        },
        () => (this.isSaving = false)
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
